import { supabase } from './supabaseClient';
import { StoryFormData, StoryResponse } from '../types/story';
import { logger } from '../utils/logger';

// Transform database story to frontend format
const transformStoryFromDB = (story: any): StoryResponse => {
  return {
    id: story.id,
    title: story.title,
    text: story.text,
    audioUrl: story.audio_url,
    setting: story.setting,
    characters: story.characters,
    mood: story.mood,
    voice: story.voice,
    length: story.length,
    backgroundNoise: story.background_noise,
    additionalDetails: story.additional_details,
    isPublic: story.is_public,
    isFavorite: story.is_favorite,
    shareCode: story.share_code,
    createdAt: story.created_at
    // updatedAt is not part of StoryResponse type according to the linter
  };
};

// ================= Story Workshop API Functions =================

// Saved Settings API methods
export const getSavedSettings = async () => {
  try {
    const { data, error } = await supabase
      .from('saved_settings')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error fetching saved settings:', error);
    throw error;
  }
};

export const createSavedSetting = async (settingData: {
  name: string;
  setting: string;
  description?: string;
  is_favorite?: boolean;
}) => {
  try {
    // Get the current user's session
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      throw new Error('You must be logged in to save settings');
    }

    const { data, error } = await supabase
      .from('saved_settings')
      .insert([{
        ...settingData,
        user_id: session.session.user.id
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error creating saved setting:', error);
    throw error;
  }
};

export const updateSavedSetting = async (id: string, updates: {
  name?: string;
  setting?: string;
  description?: string;
  is_favorite?: boolean;
}) => {
  try {
    const { data, error } = await supabase
      .from('saved_settings')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error updating saved setting:', error);
    throw error;
  }
};

export const deleteSavedSetting = async (id: string) => {
  try {
    const { error } = await supabase
      .from('saved_settings')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    logger.error('Error deleting saved setting:', error);
    throw error;
  }
};

// Saved Characters API methods
export const getSavedCharacters = async () => {
  try {
    const { data, error } = await supabase
      .from('saved_characters')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error fetching saved characters:', error);
    throw error;
  }
};

export const createSavedCharacter = async (name: string, character: string, description?: string) => {
  try {
    // Get the current user's session
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      throw new Error('You must be logged in to save characters');
    }

    const { data, error } = await supabase
      .from('saved_characters')
      .insert([{ 
        name, 
        character, 
        description,
        user_id: session.session.user.id
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error creating saved character:', error);
    throw error;
  }
};

export const updateSavedCharacter = async (id: string, updates: {
  name?: string;
  character?: string;
  description?: string;
  is_favorite?: boolean;
}) => {
  try {
    const { data, error } = await supabase
      .from('saved_characters')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error updating saved character:', error);
    throw error;
  }
};

export const deleteSavedCharacter = async (id: string) => {
  try {
    const { error } = await supabase
      .from('saved_characters')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  } catch (error) {
    logger.error('Error deleting saved character:', error);
    throw error;
  }
};

// User Story Preferences API methods
export const getUserStoryPreferences = async () => {
  try {
    const { data, error } = await supabase
      .from('user_story_preferences')
      .select('*')
      .single();
    
    if (error && error.code !== 'PGRST116') { // No rows returned is not a true error
      throw error;
    }
    return data || null;
  } catch (error) {
    logger.error('Error fetching user story preferences:', error);
    throw error;
  }
};

export const saveUserStoryPreferences = async (preferences: {
  default_mood?: string;
  default_length?: string;
  default_voice?: string;
}) => {
  try {
    // Get the current user's session
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      throw new Error('You must be logged in to save preferences');
    }
    
    // First check if preferences already exist
    const { data: existingPrefs } = await supabase
      .from('user_story_preferences')
      .select('id')
      .limit(1);
    
    let result;
    
    if (existingPrefs && existingPrefs.length > 0) {
      // Update existing preferences
      const { data, error } = await supabase
        .from('user_story_preferences')
        .update(preferences)
        .eq('id', existingPrefs[0].id)
        .select()
        .single();
      
      if (error) throw error;
      result = data;
    } else {
      // Create new preferences
      const { data, error } = await supabase
        .from('user_story_preferences')
        .insert([{
          ...preferences,
          user_id: session.session.user.id
        }])
        .select()
        .single();
      
      if (error) throw error;
      result = data;
    }
    
    return result;
  } catch (error) {
    logger.error('Error saving user story preferences:', error);
    throw error;
  }
};

// API service
export const ApiService = {
  // Get setting suggestions
  getSettingSuggestions: async (count: number = 5): Promise<string[]> => {
    try {
      logger.log('Getting setting suggestions...');
      
      // Return default AI-generated setting suggestions
      const defaultSettings = [
        "Enchanted Forest",
        "Underwater Kingdom",
        "Space Station",
        "Magical School",
        "Dragon's Lair",
        "Crystal Cave",
        "Cloud Kingdom",
        "Pirate Ship",
        "Dinosaur Valley",
        "Fairy Village"
      ];
      
      // Shuffle array to get different suggestions each time
      const shuffled = [...defaultSettings].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, count);
    } catch (error) {
      logger.error('Error getting setting suggestions:', error);
      // Return some defaults on error
      return [
        "Enchanted Forest",
        "Underwater Kingdom",
        "Space Station",
        "Magical School",
        "Dragon's Lair"
      ].slice(0, count);
    }
  },
  
  // Get character suggestions
  getCharacterSuggestions: async (count: number = 5): Promise<string[]> => {
    try {
      logger.log('Getting character suggestions...');
      
      // Return default AI-generated character suggestions
      const defaultCharacters = [
        "Brave Knight",
        "Clever Fox",
        "Friendly Dragon",
        "Magical Unicorn",
        "Wise Owl",
        "Mischievous Mouse",
        "Curious Rabbit",
        "Adventurous Girl",
        "Kind Wizard",
        "Playful Puppy"
      ];
      
      // Shuffle array to get different suggestions each time
      const shuffled = [...defaultCharacters].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, count);
    } catch (error) {
      logger.error('Error getting character suggestions:', error);
      // Return some defaults on error
      return [
        "Brave Knight",
        "Clever Fox",
        "Friendly Dragon",
        "Magical Unicorn",
        "Wise Owl"
      ].slice(0, count);
    }
  },
  
  // Generate a new story
  generateStory: async (formData: StoryFormData): Promise<{ id: string; status: string; message: string }> => {
    try {
      logger.log('Generating and storing story...');
      
      // Get the current user's session
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to create a story');
      }
      
      const userId = session.session.user.id;
      
      // Process the enhanced setting and characters for better formatting in the story
      const formattedSetting = formData.setting;
      const formattedCharacters = formData.characters.join(' and ');
      
      // Create a more detailed story template that uses the enhanced data
      let storyText = `Once upon a time in ${formattedSetting}, there was an adventure featuring ${formattedCharacters}. `;
      storyText += `The story was ${formData.mood} and had a ${formData.length} length. `;
      
      if (formData.additionalDetails) {
        storyText += formData.additionalDetails;
      }
      
      // Generate a share code
      const shareCode = Math.random().toString(36).substring(2, 10);
      
      // Create a timestamp for created_at
      const timestamp = new Date().toISOString();
      
      // Create the story data object
      const storyData = {
        title: formData.title || "Your Magical Adventure",
        text: storyText,
        audio_url: null, // No audio when using direct DB method
        setting: formData.setting,
        characters: formData.characters,
        mood: formData.mood,
        voice: formData.voice,
        length: formData.length,
        background_noise: formData.backgroundNoise,
        additional_details: formData.additionalDetails,
        is_public: false,
        is_favorite: false,
        share_code: shareCode,
        user_id: userId,
        created_at: timestamp
      };
      
      // STEP 1: Insert the story without trying to return data
      logger.log('Step 1: Inserting story into database...');
      const { error: insertError } = await supabase
        .from('stories')
        .insert(storyData);
      
      if (insertError) {
        logger.error('Error inserting story:', insertError);
        throw new Error(`Failed to create story: ${insertError.message}`);
      }
      
      // STEP 2: Query for the most recently created story by this user
      logger.log('Step 2: Retrieving the newly created story...');
      const { data: recentStories, error: queryError } = await supabase
        .from('stories')
        .select('id')
        .eq('user_id', userId)
        .eq('share_code', shareCode) // Use the unique share code as an additional filter
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (queryError) {
        logger.error('Error querying for new story:', queryError);
        throw new Error(`Story was created but couldn't be retrieved: ${queryError.message}`);
      }
      
      if (!recentStories || recentStories.length === 0) {
        logger.error('No stories found after creation');
        throw new Error('Story was created but couldn\'t be found');
      }
      
      // Return the ID of the most recently created story
      return {
        id: recentStories[0].id,
        status: 'success',
        message: 'Story created successfully'
      };
    } catch (error) {
      logger.error('Error in generateStory:', error);
      throw error;
    }
  },
  
  // Get all stories for the current user
  getUserStories: async (): Promise<StoryResponse[]> => {
    try {
      logger.log('Fetching user stories directly with Supabase client...');
      
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to view your stories');
      }
      
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('user_id', session.session.user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw new Error(`Failed to fetch user stories: ${error.message}`);
      }
      
      if (!data || !Array.isArray(data)) {
        logger.error('Supabase returned non-array data:', data);
        return [];
      }
      
      // Transform database fields to client format
      return data.map(transformStoryFromDB);
    } catch (error) {
      logger.error('Error fetching user stories:', error);
      throw error;
    }
  },
  
  // Get a single story by ID
  getStory: async (id: string): Promise<StoryResponse> => {
    try {
      logger.log(`Fetching story with ID ${id} directly with Supabase client...`);
      
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to view your stories');
      }
      
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('id', id)
        .eq('user_id', session.session.user.id)
        .single();
      
      if (error) {
        throw new Error(`Failed to fetch story: ${error.message}`);
      }
      
      if (!data) {
        throw new Error('Story not found');
      }
      
      // Transform database fields to client format
      return transformStoryFromDB(data);
    } catch (error) {
      logger.error('Error fetching story:', error);
      throw error;
    }
  },
  
  // Get a shared story by share code
  getSharedStory: async (shareCode: string): Promise<StoryResponse> => {
    try {
      logger.log(`Fetching shared story with code ${shareCode} directly with Supabase client...`);
      
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('share_code', shareCode)
        .eq('is_public', true)
        .single();
      
      if (error) {
        throw new Error(`Failed to fetch shared story: ${error.message}`);
      }
      
      if (!data) {
        throw new Error('Shared story not found or is not public');
      }
      
      // Transform database fields to client format
      return transformStoryFromDB(data);
    } catch (error) {
      logger.error('Error fetching shared story:', error);
      throw error;
    }
  },
  
  // Get favorite stories
  getFavoriteStories: async (): Promise<StoryResponse[]> => {
    try {
      logger.log('Fetching favorite stories directly with Supabase client...');
      
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to view your favorite stories');
      }
      
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('user_id', session.session.user.id)
        .eq('is_favorite', true)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw new Error(`Failed to fetch favorite stories: ${error.message}`);
      }
      
      if (!data || !Array.isArray(data)) {
        logger.error('Supabase returned non-array data:', data);
        return [];
      }
      
      // Transform database fields to client format
      return data.map(transformStoryFromDB);
    } catch (error) {
      logger.error('Error fetching favorite stories:', error);
      throw error;
    }
  },
  
  // Update story favorite status
  updateStoryFavorite: async (storyId: string, isFavorite: boolean): Promise<StoryResponse> => {
    try {
      logger.log(`Updating favorite status of story ${storyId} to ${isFavorite} directly with Supabase client...`);
      
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to update a story');
      }
      
      const { data, error } = await supabase
        .from('stories')
        .update({ is_favorite: isFavorite })
        .eq('id', storyId)
        .eq('user_id', session.session.user.id)
        .select()
        .single();
      
      if (error) {
        throw new Error(`Failed to update story favorite status: ${error.message}`);
      }
      
      if (!data) {
        throw new Error('Story not found or you do not have permission to modify it');
      }
      
      // Transform database fields to client format
      return transformStoryFromDB(data);
    } catch (error) {
      logger.error('Error updating story favorite status:', error);
      throw error;
    }
  },
  
  // Update story visibility
  updateStoryVisibility: async (storyId: string, isPublic: boolean): Promise<StoryResponse> => {
    try {
      logger.log(`Updating visibility of story ${storyId} to ${isPublic ? 'public' : 'private'} directly with Supabase client...`);
      
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to update a story');
      }
      
      const { data, error } = await supabase
        .from('stories')
        .update({ is_public: isPublic })
        .eq('id', storyId)
        .eq('user_id', session.session.user.id)
        .select()
        .single();
      
      if (error) {
        throw new Error(`Failed to update story visibility: ${error.message}`);
      }
      
      if (!data) {
        throw new Error('Story not found or you do not have permission to modify it');
      }
      
      // Transform database fields to client format
      return transformStoryFromDB(data);
    } catch (error) {
      logger.error('Error updating story visibility:', error);
      throw error;
    }
  },
  
  // Delete a story
  deleteStory: async (storyId: string): Promise<void> => {
    try {
      logger.log(`Deleting story ${storyId} directly with Supabase client...`);
      
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to delete a story');
      }
      
      const { error } = await supabase
        .from('stories')
        .delete()
        .eq('id', storyId)
        .eq('user_id', session.session.user.id);
      
      if (error) {
        throw new Error(`Failed to delete story: ${error.message}`);
      }
    } catch (error) {
      logger.error('Error deleting story:', error);
      throw error;
    }
  },
  
  // Story Workshop methods
  getSavedSettings,
  createSavedSetting,
  updateSavedSetting,
  deleteSavedSetting,
  getSavedCharacters,
  createSavedCharacter,
  updateSavedCharacter,
  deleteSavedCharacter,
  getUserStoryPreferences,
  saveUserStoryPreferences,
}; 